// Shared currency-value helpers for CurrencyInput consumers (Phase A promotion,
// module 11 polish pass). The canonical UI value shape is a decimal-dollars
// string with NO "$" and NO commas: "", "0", "1234.5", "1234.50".
//
// Backend units differ per consumer (ChamberSettings stores decimal DOLLARS,
// Events/Invoicing store CENTS) — apply these adapters ONLY at each call
// boundary; never centralize unit conversion inside the input component.
//
// Read-only currency display stays with formatters.ts (cmpCurrencyFormatter);
// these helpers own the editable-value shape only.

/**
 * Keystroke sanitizer (logic promoted verbatim from ChamberSettings
 * numericInputs.sanitizeDecimalInput): strip everything but digits and dots,
 * first dot wins, decimals clamped to 2. Admits no "-", so values are never
 * negative.
 */
export const sanitizeCurrencyValue = (raw: string): string => {
  const cleaned = raw.replace(/[^0-9.]/g, "");
  const [whole, ...decimals] = cleaned.split(".");
  if (decimals.length === 0) {
    return whole;
  }
  return `${whole}.${decimals.join("").slice(0, 2)}`;
};

/**
 * Blur normalizer: "" for empty/unparseable input, otherwise the canonical
 * un-grouped 2-decimal string ("1234.00"). Grouping (commas) exists only in
 * CurrencyInput's display state, never in the emitted value.
 */
export const formatCurrencyValueOnBlur = (value: string): string => {
  const numeric = Number.parseFloat(sanitizeCurrencyValue(value));
  if (Number.isNaN(numeric)) {
    return "";
  }
  return numeric.toFixed(2);
};

/**
 * Canonical value -> integer cents (promoted verbatim from
 * eventSalesShared.dollarsToCents): non-finite -> 0, clamped >= 0, rounded.
 */
export const currencyValueToCents = (value: string): number => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return 0;
  }
  return Math.max(0, Math.round(numeric * 100));
};

/**
 * Integer cents -> canonical value (promoted verbatim from
 * eventSalesShared.centsToDollarsString); null/undefined -> "".
 */
export const centsToCurrencyValue = (cents?: number | null): string => {
  if (cents === null || cents === undefined) {
    return "";
  }
  return (Math.max(0, cents) / 100).toFixed(2);
};

/**
 * Canonical value -> decimal dollars number for the ChamberSettings
 * `...InDollars` backends: parseFloat, NaN -> 0.
 */
export const currencyValueToDollarsNumber = (value: string): number => {
  const numeric = Number.parseFloat(value);
  return Number.isNaN(numeric) ? 0 : numeric;
};

/**
 * Decimal dollars number -> canonical value; null/undefined -> "".
 */
export const dollarsNumberToCurrencyValue = (
  dollars?: number | null
): string => {
  if (dollars === null || dollars === undefined) {
    return "";
  }
  return dollars.toFixed(2);
};
